import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Api from "../../../../api/api";
import { IconPlus, IconCopy, IconRefresh } from "@tabler/icons-react";
import SkeletonLoader from "../../../../components/SkeletonLoader";
import { IonAlert, IonToggle, isPlatform, useIonToast } from "@ionic/react";
interface ApiKey {
  apiKey: string;
  name: string;
  active: boolean;
}

const isMobile = isPlatform("android") || isPlatform("ios");

const ApiKeys: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const [present] = useIonToast();
  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [copied, setCopied] = useState(false);

  const { formatMessage } = useIntl();

  const handleCopy = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy apiKey", err);
    }
  };

  const getApiKeys = async () => {
    const { data, err } = await Api.getKeys();
    if (err) {
      console.error("Error fetching API keys:", err);
    }
    if (data) {
      setApiKeys(data.keys || []);
    }
    console.log("API Keys:", data);
    setLoading(false);
    setWorking(false);
  };

  const onCreateApiKey = async () => {
    setWorking(true);
    const { data, err } = await Api.createKey();

    if (data) {
      present({
        message: formatMessage({ id: "common.success" }),
        duration: 2000,
        color: "success",
        cssClass: "success-toast",
        position: "top",
      });
    }
    if (err) {
      present({
        message: formatMessage({ id: "common.tryItLater" }),
        duration: 2000,
        color: "danger",
        position: "top",
      });
    }
    getApiKeys();
  };

  const onToggleApiKey = async (key: ApiKey) => {
    setWorking(true);
    const { data, err } = await Api.updateKey(key.apiKey);

    if (data) {
      present({
        message: formatMessage({ id: "common.success" }),
        duration: 2000,
        color: "success",
        position: "top",
        cssClass: "success-toast",
      });
    }
    if (err) {
      present({
        message: formatMessage({ id: "common.tryItLater" }),
        duration: 2000,
        color: "danger",
        position: "top",
      });
    }
    getApiKeys();
  };

  const onBeforeRefresh = () => {
    setOpenAlert(true);
  };

  const onHideAlert = () => {
    setOpenAlert(false);
  };

  const onRefreshApiKey = async () => {
    setWorking(true);
    const { data, err } = await Api.refreshKey("random_for_now");

    if (data) {
      present({
        message: formatMessage({ id: "common.success" }),
        duration: 10000,
        color: "success",
        position: "top",
        cssClass: "success-toast",
      });
    }
    if (err) {
      present({
        message: formatMessage({ id: "common.tryItLater" }),
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
    getApiKeys();
  };

  useEffect(() => {
    getApiKeys();
  }, []);

  return (
    <div className={`settings-presentation`}>
      <h4>
        <FormattedMessage id="api.title" />
      </h4>
      <p className="color-step-700 fs-3">
        <FormattedMessage id="api.subTitle" />
      </p>
      <hr />
      {loading ? (
        <SkeletonLoader rows={1} />
      ) : apiKeys.length ? (
        <table className="table align-middle">
          <thead>
            <tr>
              <th style={{ width: "70%" }}>API Key</th>
              <th style={{ width: "15%" }} className="text-center">
                <FormattedMessage id="common.regenerate" />
              </th>
              <th style={{ width: "15%" }} className="text-center">
                <FormattedMessage id="common.active" />
              </th>
            </tr>
          </thead>

          <tbody>
            {apiKeys.map((item: ApiKey, index) => (
              <tr key={index}>
                {/* API KEY + Copy */}
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <span className="color-step-750">{item.apiKey}</span>

                    <button
                      onClick={() => handleCopy(item.apiKey)}
                      className="btn btn-visito-light btn-sm"
                    >
                      {copied ? "Copied!" : <IconCopy size={16} />}
                    </button>
                  </div>
                </td>
                <td className="text-center">
                  <button
                    onClick={() => onBeforeRefresh()}
                    className="btn btn-visito-light btn-sm"
                    disabled={working}
                  >
                    <IconRefresh size={16} />
                  </button>
                </td>
                <td className="text-center">
                  <div className="h-100 d-flex align-items-center justify-content-center">
                    <IonToggle
                      {...(isMobile
                        ? { className: "visito-toggle" }
                        : { mode: "ios", className: "visito-toggle ios" })}
                      checked={item.active}
                      disabled={working}
                      onIonChange={() => onToggleApiKey(item)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div>
          <button
            disabled={working}
            className="btn btn-visito-primary btn-sm btn-flex btn-icon"
            onClick={() => onCreateApiKey()}
          >
            {working ? (
              "..."
            ) : (
              <>
                <IconPlus size="16" />
                <FormattedMessage id="common.create" />
              </>
            )}
          </button>
        </div>
      )}
      <IonAlert
        isOpen={openAlert}
        header={formatMessage({ id: "api.regenerate.warningTitle" })}
        message={formatMessage({ id: "api.regenerate.warningDesc" })}
        buttons={[
          {
            text: formatMessage({ id: "common.cancel" }),
            role: "cancel",
            handler: onHideAlert,
            cssClass: "btn btn-visito-light",
          },
          {
            text: formatMessage({ id: "common.continue" }),
            role: "confirm",
            handler: onRefreshApiKey,
            cssClass: "btn btn-visito-primary",
          },
        ]}
        onDidDismiss={onHideAlert}
      />
    </div>
  );
};

interface ILayoutProps {}

export default ApiKeys;
